import React from 'react';
import styled from 'styled-components';

import { getSearchLandingFeedSubSectionTrackingInfo } from 'homepage/utils/getSearchLandingFeedSubsectionTrackingInfo';
import { variableFontRegular } from 'styles/theme/text';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useTranslation } from 'react-i18next';

import { ICuratedLinkSection } from 'homepage/types/CuratedLink';
import { IModuleTrackingData } from 'homepage/types/ModuleTrackingData';
import CuratedLinkTableRow from './CuratedLinkTableRow';

interface ICuratedLinkTable {
  moduleTrackingData: IModuleTrackingData;
  curatedLinks: ICuratedLinkSection[];
  pageOffset?: number;
}

/**
 * [CuratedLinkTable]
 *  displays a Table of CuratedLinkSections
 * @param {Object} curatedLinks A list of 7 CuratedLinkSections.
 */

export default function CuratedLinkTable({
  moduleTrackingData,
  curatedLinks,
  pageOffset = 0,
}: ICuratedLinkTable) {
  const { t } = useTranslation();

  const handleSubSectionClick = (itemIndex = 0, curatedLink) => {
    const index = itemIndex + pageOffset;

    sendTrackingEvent('CMS_MODULE_TAP', {
      ...getSearchLandingFeedSubSectionTrackingInfo(moduleTrackingData, curatedLink, index),
    });
  };

  return (
    <CuratedLinkTable.Wrapper>
      <CuratedLinkTable.Table>
        <tbody>
          <CuratedLinkTable.Row>
            <CuratedLinkTable.NameHeader>
              {t(`homepage.module.name`, `Name`)}
            </CuratedLinkTable.NameHeader>
            <CuratedLinkTable.YearHeader>
              {t(`homepage.module.year`, `Year`)}
            </CuratedLinkTable.YearHeader>
            <CuratedLinkTable.LocationHeader>
              {t(`homepage.module.location`, `Location`)}
            </CuratedLinkTable.LocationHeader>
            <CuratedLinkTable.ItemHeader>
              {t(`homepage.module.items`, `Items`)}
            </CuratedLinkTable.ItemHeader>
          </CuratedLinkTable.Row>
          {curatedLinks.map((curatedLink, index) => (
            <CuratedLinkTableRow
              key={`${curatedLink?.title}-${(curatedLink?.count || index) * index}`}
              onClick={() => handleSubSectionClick(index, curatedLink)}
              {...curatedLink}
            />
          ))}
        </tbody>
      </CuratedLinkTable.Table>
    </CuratedLinkTable.Wrapper>
  );
}

CuratedLinkTable.Wrapper = styled.div``;
CuratedLinkTable.Table = styled.table`
  overflow: hidden;
  margin: 0;
  padding: 0;
  margin-right: 24px;
  width: 322px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: 639px;
  }
`;

CuratedLinkTable.Row = styled.tr`
  display: inline-block;
  border-bottom: ${({ theme }) => theme.borders.white};
  width: 322px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: 639px;
  }
`;

CuratedLinkTable.TableHeader = styled.th`
  font-weight: 400;
  padding: 8px 0px;
  text-transform: uppercase;
  ${variableFontRegular({ size: '9.5px', height: '11px' })}
`;

CuratedLinkTable.NameHeader = styled(CuratedLinkTable.TableHeader)`
  width: 169px;
  padding-right: 40px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: 221px;
  }
`;

CuratedLinkTable.YearHeader = styled(CuratedLinkTable.TableHeader)`
  width: 43px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: 111px;
  }
`;

CuratedLinkTable.LocationHeader = styled(CuratedLinkTable.TableHeader)`
  width: 67px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: 221px;
  }
`;

CuratedLinkTable.ItemHeader = styled(CuratedLinkTable.TableHeader)`
  width: 42px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: 87px;
  }
`;
